import { Route } from '../enums/route.enum';
import { UserRole } from '../enums/user-roles.enum';
import { normalizePortalRole, type PortalRole } from '../../services/user/user.service';
import type { Route as NgRoute, Routes } from '@angular/router';

type AllowedRolesData = { allowedRoles?: unknown };

/**
 * Converts a raw `allowedRoles` value (from route data) into a typed array of PortalRole.
 * Non-array inputs return an empty array; invalid items are filtered out.
 */
function normalizeAllowedRoles(raw: unknown): PortalRole[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item) => normalizePortalRole(item))
    .filter((role): role is PortalRole => role !== null);
}

/** Extracts the normalized list of allowed roles from a route's `data.allowedRoles` property. */
function getAllowedRolesFromRoute(route: NgRoute): PortalRole[] {
  const data = route.data as AllowedRolesData | undefined;
  return normalizeAllowedRoles(data?.allowedRoles);
}

/**
 * Checks basic role-based access: returns true if the route has no role restrictions
 * or if the given role is included in the route's allowed roles.
 */
function hasAccess(role: PortalRole, route: NgRoute): boolean {
  const allowed = getAllowedRolesFromRoute(route);
  return allowed.length === 0 ? true : allowed.includes(role);
}

/**
 * Route accessibility with optional "party search" context.
 *
 * Some routes are annotated with `data.requiresPartySearch = true` and are only meaningful
 * when an admin/CNH user is actively impersonating a customer (party search context present).
 * For normal `User` role, the flag is ignored to avoid accidentally blocking standard navigation.
 */
function isRouteAccessibleForPartyContext(
  role: PortalRole,
  route: NgRoute,
  partySearchActive: boolean,
): boolean {
  if (!hasAccess(role, route)) {
    return false;
  }
  const requiresParty = route.data?.['requiresPartySearch'] === true;
  if (requiresParty && role !== UserRole.User) {
    // Internal portal: FAQ, Contact & Discover are usable without impersonation (design + ops).
    const routePath = route.path;
    if (
      (role === UserRole.Admin || role === UserRole.CNHUser) &&
      (routePath === Route.FAQ || routePath === Route.CONTACT_US || routePath === Route.DISCOVER)
    ) {
      return true;
    }
    return partySearchActive;
  }
  return true;
}

/** Recursively flattens a nested Angular route tree into a single-level array. */
function flattenRoutes(routes: Routes): NgRoute[] {
  const out: NgRoute[] = [];
  const visit = (rts: Routes) => {
    for (const r of rts) {
      out.push(r);
      if (r.children) {
        visit(r.children);
      }
    }
  };
  visit(routes);
  return out;
}

const DEFAULT_ROUTE_PRIORITY: readonly Route[] = [
  Route.DASHBOARD,
  Route.PAYOUT,
  Route.CHANGE_PAYMENT_METHOD,
  Route.REPAYMENT_SCHEDULE,
  Route.STATEMENT,
  Route.FAQ,
  Route.CONTACT_US,
  Route.DISCOVER,
  Route.SEARCH_USER,
  Route.MY_PROFILE,
  Route.DARK_MODE,
];

const MARKETING_ADMIN_LANDING_PRIORITY: readonly Route[] = [
  Route.ANNOUNCEMENT,
  Route.UPLOAD_MARKETING_BANNER,
  Route.NOTIFICATIONS,
];

const USER_MANAGER_LANDING_PRIORITY: readonly Route[] = [Route.USER_MANAGEMENT];

/** Route order for post-login / “home” resolution: internal users land on party search first. */
export function routePriorityForRoleLanding(role: PortalRole): readonly Route[] {
  if (role === UserRole.Admin || role === UserRole.CNHUser) {
    return [
      Route.SEARCH_USER,
      ...DEFAULT_ROUTE_PRIORITY.filter((userRoute) => userRoute !== Route.SEARCH_USER),
    ];
  }
  if (role === UserRole.CNHMarketingUser) {
    return MARKETING_ADMIN_LANDING_PRIORITY;
  }
  if (role === UserRole.CNHNewProfileUser) {
    return USER_MANAGER_LANDING_PRIORITY;
  }
  return DEFAULT_ROUTE_PRIORITY;
}

/**
 * Determines whether a given role can access a specific route path.
 * Considers both role-based restrictions and party-search context requirements.
 * Returns true for routes not found in the config (access control deferred to route guards).
 */
export function canRoleAccessRoutePath(
  role: PortalRole,
  routerConfig: Routes,
  path: Route,
  partySearchActive = false,
): boolean {
  const all = flattenRoutes(routerConfig);
  const match = all.find((r) => r.path === path && !r.redirectTo);
  // Preserve previous sidebar behavior: if the route isn't present in router config,
  // don't hide the link purely due to missing metadata.
  // Actual access control is still enforced by the route guard when navigation matches.
  return match ? isRouteAccessibleForPartyContext(role, match, partySearchActive) : true;
}

/**
 * Returns the first route the user can actually navigate to.
 *
 * The function checks the preferred route order first so landing/navigation behavior stays
 * consistent across roles. If none of the preferred routes are available, it falls back to the
 * first non-login, non-wildcard route from the router config that passes role and party-search
 * access checks.
 */
export function pickFirstAccessibleRoutePath(
  role: PortalRole,
  routerConfig: Routes,
  priority: readonly Route[] = DEFAULT_ROUTE_PRIORITY,
  partySearchActive = false,
): Route | null {
  const all = flattenRoutes(routerConfig);

  for (const candidate of priority) {
    const match = all.find(
      (r) =>
        r.path === candidate &&
        !r.redirectTo &&
        // Ignore login if it is accidentally included in the preferred route list.
        r.path !== Route.LOGIN,
    );
    if (match && isRouteAccessibleForPartyContext(role, match, partySearchActive)) {
      return candidate;
    }
  }

  // Fall back to the first concrete, accessible application route in router order.
  const first = all.find(
    (r) =>
      typeof r.path === 'string' &&
      r.path.length > 0 &&
      r.path !== Route.LOGIN &&
      r.path !== '**' &&
      !r.redirectTo &&
      isRouteAccessibleForPartyContext(role, r, partySearchActive),
  );

  return (first?.path as Route | undefined) ?? null;
}

/** Extracts the first path segment from a URL, stripping query params and leading slashes. */
function firstUrlPathSegment(url: string): string {
  const path = url.split('?')[0].replace(/^\//, '');
  return path.split('/')[0] ?? '';
}

/** Party-search routes that Admin may stay on after a full page reload. */
const ADMIN_RELOAD_EXEMPT_ROUTES = new Set<string>([Route.FAQ, Route.CONTACT_US, Route.DISCOVER]);
/**
 * Whether a full page reload should send an internal user to Search.
 *
 * - **CNHUser**: party impersonation lives in memory only — any non-search URL restarts at Search.
 * - **Admin**: portal routes (user management, configuration, …) stay put; customer/contract URLs
 *   that require party context redirect to Search (aligned with {@link internalPartyContextGuard}).
 */
export function internalReloadRedirectsToSearch(
  role: PortalRole,
  routerConfig: Routes,
  url: string,
): boolean {
  if (role !== UserRole.CNHUser && role !== UserRole.Admin) {
    return false;
  }

  const segment = firstUrlPathSegment(url);
  if (segment === Route.SEARCH_USER) {
    return false;
  }

  if (role === UserRole.CNHUser) {
    return true;
  }

  if (!segment) {
    return false;
  }

  const match = flattenRoutes(routerConfig).find((r) => r.path === segment && !r.redirectTo);
  if (!match || match.data?.['requiresPartySearch'] !== true) {
    return false;
  }

  return !ADMIN_RELOAD_EXEMPT_ROUTES.has(segment);
}
